/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';

import template from './creative-list-page.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {CreativeListFiltersComponent} from './creative-list-filters.component';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {CreativeBulkActionService} from './creative-bulk-action.service';
import {CreativeAttachModalService} from './creative-attach-modal.service';
import {CreativeCopyInBulkModalService} from './creative-copy-in-bulk-modal.service';
import {asUrlGroupList, asUrlList} from './creative-url-utils';
import {LocalNetworkInfo} from '../../components/session/local-network-info';

import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {configureCreativeList, enrichCreativeRows} from './creative-commons';
import {StateService} from '@uirouter/core';
import {CreativeApiService, CreativeModel} from './creative-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, CreativeListFiltersComponent],
  selector: 'adn-creative-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreativeListPageComponent implements OnInit {
  @Input() list: SignalAdminResourceListState<CreativeModel>;

  @Input()
  set readOnly(value: boolean) {
    (this.list || this.defaultList).readOnly.set(value);
  }
  @Input() calledFrom: string;

  readonly itemStateName = 'app.adv.creatives.creative';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly lineItemItemStateName = 'app.adv.line-items.line-item';
  readonly creativeSetItemStateName = 'app.adv.creative-sets.creative-set';
  readonly externalDemandSourceItemStateName = 'app.adm.external-demand-sources.external-demand-source';

  hasCreativeSet = false;
  attachSuffix = 'Line Item';
  readonly selector = {outputFields: [] as any[], selFields: [] as string[]};
  readonly creativeFields: any[];
  readonly defaultFields: any[];
  readonly perms: any;
  private readonly stateService = inject(StateService);
  private readonly api = inject(CreativeApiService);
  private readonly defaultList = inject(SignalAdminResourceListStateFactory).create<CreativeModel>(
    this.api,
    {
      listId: 'fullCreativeList',
      baseTable: 'creative',
      transformResults: (items) => {
        enrichCreativeRows(items, this.stateService);
        return items;
      }
    },
    inject(DestroyRef)
  );

  constructor(
    @Inject(LocalNetworkProfile) localNetworkProfile: any,
    @Inject(LocalUserPermissions) localUserPermissions: any,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly creativeBulkActionService: CreativeBulkActionService,
    private readonly creativeAttachModalService: CreativeAttachModalService,
    private readonly creativeCopyInBulkModalService: CreativeCopyInBulkModalService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.list = this.defaultList;
    const config: any = {};
    configureCreativeList(config, localNetworkProfile);
    this.creativeFields = config.creativeFields;
    this.defaultFields = config.defaultFields;
    this.perms = localUserPermissions.getAllVisPermissions();
  }

  ngOnInit(): void {
    this.hasCreativeSet = this.localNetworkInfo.getInfo('creativeSetCount') > 0;
    this.attachSuffix = 'Line Item' + (this.hasCreativeSet ? ' or Creative Set' : '');
    this.list.load();
  }

  get visibleColumns(): SignalAdminListColumn[] {
    const fields = this.selector.outputFields.length ? this.selector.outputFields : this.defaultFields;
    return this.normalizeColumns(fields);
  }

  isFieldVisible(param: string): boolean {
    const selected = this.selector.selFields;
    const fields = selected.length ? selected : this.defaultFields.map((field) => this.fieldId(field));
    return fields.includes(param);
  }

  layoutLinkDisabled(): boolean {
    return this.perms.MANAGE_LAYOUTS !== true;
  }

  previewParams(creative: any): any {
    return {
      creativeId: creative.id,
      networkId: creative.network?.id,
      creativeWidth: creative.width,
      creativeHeight: creative.height,
      lineItemId: creative.lineItemId || creative.lineItem?.id,
      lineItemCode: creative.lineItemCode || creative.lineItem?.lineItemCode
    };
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }

  impressionTrackingUrlList(creative: any): string[] {
    return asUrlList(creative?.impressionTrackingUrls);
  }

  openBulkActions(): void {
    this.creativeBulkActionService.createBulkAction(this.bulkAdapter())();
  }

  attach(): void {
    this.creativeAttachModalService.open(this.bulkAdapter(), this.Creative, () => this.afterBulkAction());
  }

  copyInBulk(): void {
    this.creativeCopyInBulkModalService.open(this.bulkAdapter(), this.Creative, () => this.afterBulkAction());
  }

  private bulkAdapter(): any {
    return {
      bulkSelection: this.list.selected(),
      creativeType: 'CREATIVE',
      disabled: false,
      miscBulkAction: (modal: any, controller: any, items: Partial<CreativeModel>[]) => {
        this.api.bulkSave(items).subscribe({
          next: () => {
            modal.close();
            this.afterBulkAction();
          },
          error: () => {
            controller.form = controller.form || {};
            controller.form.bulkError = true;
          }
        });
      }
    };
  }

  private afterBulkAction(): void {
    this.list.deselectAll();
    this.list.load();
  }

  private normalizeColumns(fields: any[]): SignalAdminListColumn[] {
    return fields.map((field) => typeof field === 'string' ? {param: field} : {
      param: field.param ?? field.label ?? '',
      label: field.label,
      labelInstant: field.labelInstant,
      numeric: field.numeric,
      sortable: field.sortable
    });
  }

  private fieldId(field: any): string {
    return typeof field === 'string' ? field : field.param || field.label || field;
  }
}
